/**
 * Ein Lauf im Detail: /runs/:id.
 *
 * Bis zum 26.09.2026 stand unter Läufe nur, was lief und was es kostete.
 * Was die Personas gesagt haben, lag in der Unterhaltung mit Claude oder in
 * einem veröffentlichten Bericht. Wer Wochen später ein Marktergebnis meldet,
 * sitzt aber nicht mehr in dieser Unterhaltung.
 *
 * WOHER DIE DATEN KOMMEN: `GET /billing/run?id=` (`readRun` in
 * `supabase/functions/_shared/sim/uebersicht.ts`). Das Konto kommt dort aus
 * der Sitzung, nie aus der Adresse; ein fremder Lauf antwortet wie ein
 * fehlender mit 404. Welche Felder herausgehen, entscheidet der Server.
 *
 * VOR DEM DEPLOY der Route antwortet die billing-Function mit 404 „Not
 * found" (unbekannte Route). Die Seite sagt dann, dass die Ansicht noch nicht
 * freigeschaltet ist, statt jeden Lauf für fremd zu erklären.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { ApiFehler, ladeLauf, type LaufDetail as Detail, type Rueckmeldung } from '../api';
import { NotenJePersona, RandEtikett, notenSatz } from '../noten-je-persona';
import { personaNoten } from '../bericht-noten';
import { bilanzSatz, kernZuerst, leseBilanz, leseKernRand, leseMuster, musterSatz, randZeile } from '../kern-rand';
import { fassungsname, istSeitentest } from '../fassungsname';
import { sichererLink } from '../sicherer-link';
import { useSitzung } from '../session';
import { Abschnitt, AppRahmen, Hinweis, KleinerKnopf, Kopierzeile, Marke, Panel, Platzhalter, Seitenkopf } from '../ui';
import { CHALLENGER } from '../preise';
import ErgebnisMelden from './ErgebnisMelden';
import { FassungenAnsicht } from '../fassungen-ansicht';
import { locale, sprache, t } from '../sprache';

/** Die Textarten der Werkzeuge (ASSET_ENUM in tools.ts), auf Deutsch. Unbekanntes bleibt, wie es kommt. */
export const TEXTART: Record<string, string> = {
  headline: 'Headline', ad: 'Anzeige', landing_page: 'Landingpage', email: 'E-Mail', subject_line: 'Betreffzeile',
};
export const TEXTART_EN: Record<string, string> = {
  headline: 'Headline', ad: 'Ad', landing_page: 'Landing page', email: 'Email', subject_line: 'Subject line',
};
/** Die Textarten in der Sprache der App, beim Rendern gewählt. */
export const textart = (): Record<string, string> => (sprache() === 'en' ? TEXTART_EN : TEXTART);

const zahl = (n: number) => new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

const zeitpunkt = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: '2-digit' })
      + ', ' + d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
};

export default function LaufDetail() {
  const { id = '' } = useParams();
  const { session, abmelden } = useSitzung();
  const [lauf, setLauf] = useState<Detail | null>(null);
  const [fehler, setFehler] = useState<ApiFehler | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [melden, setMelden] = useState(false);
  const [antwort, setAntwort] = useState<{ grund: string; zaehlbar: boolean; quote: string } | null>(null);

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      setLauf(await ladeLauf(id));
    } catch (e) {
      const f = e instanceof ApiFehler ? e : new ApiFehler((e as Error).message);
      setFehler(f);
      if (f.abgemeldet) void abmelden();
    } finally {
      setLaedt(false);
    }
  }, [id, abmelden]);

  useEffect(() => { void holen(); }, [holen]);
  useEffect(() => { if (lauf) document.title = `${titelVon(lauf)} · 99takes`; }, [lauf]);

  const zurueck = (
    <Link to="/runs" className="text-[0.875rem] text-text-hell no-underline hover:text-marke-tief">{t('← Alle Runs', '← All runs')}</Link>
  );

  return (
    <AppRahmen email={session?.user?.email ?? ''} abmelden={() => void abmelden()}>
      <div className="mb-3">{zurueck}</div>

      {laedt && !lauf && (
        <>
          <Seitenkopf titel="Run" />
          <Panel className="p-4 sm:p-5"><Platzhalter zeilen={4} /></Panel>
        </>
      )}

      {!laedt && fehler && !lauf && <Fehler f={fehler} />}

      {lauf && (
        <>
          {/* Aufbau nach Entwurf 1c („Feinschliff Oktober", Claude-Design-Projekt
              7f590d08-…, gewaehlt von Robert am 04.10.2026): das Urteil traegt
              die beiden Fassungen selbst, man sieht, was gewonnen hat, neben
              dem Satz. Danach die Einwaende mit dem naechsten Schritt, die
              Noten je Persona als Beleg zugeklappt. Der Text steht als
              Seitentitel nur fuer Screenreader und den Tab, sichtbar steht er
              in der Karte. */}
          <h1 className="sr-only">{titelVon(lauf)}</h1>
          <p className="mt-0 mb-3 text-[0.8125rem] leading-relaxed text-text-hell"><Kopfzeile l={lauf} /></p>

          {antwort && <Hinweis art={antwort.zaehlbar ? 'gut' : 'info'}>{antwort.grund} {antwort.quote}</Hinweis>}

          <Urteil l={lauf} texte={<FassungenImUrteil l={lauf} />} markt={
            lauf.mode !== 'compare' || lauf.stufe !== 'full' || !lauf.scores ? null
              : melden
                ? (
                  <div className="mt-4 rounded-md border border-marke/40 bg-flaeche p-4">
                    <ErgebnisMelden
                      lauf={lauf}
                      abbrechen={() => setMelden(false)}
                      fertig={a => { setMelden(false); setAntwort(a); void holen(); }}
                    />
                  </div>
                )
                : <Markt l={lauf} melden={() => { setAntwort(null); setMelden(true); }} />
          } />

          {lauf.geschaerft && (
            <div className="mt-6">
              <Hinweis art="info">
                {lauf.geschaerft.gleiche_jury
                  ? t('An der Jury geschärft: 99takes hat diesen Text aus den Einwänden genau dieser Personas geschrieben. Ein Sieg hier ist erwartbar und noch kein Beleg für den Markt; entschieden wird mit dem echten Ergebnis.', 'Tuned to the jury: 99takes wrote this copy from the objections of exactly these personas. A win here is expected and not yet evidence for the market. The real result decides.')
                  : t('Diesen Text hat 99takes geschrieben, aber nicht aus den Einwänden dieser Personas. Der Vergleich ist nicht vorab geschärft.', '99takes wrote this copy, but not from the objections of these personas. The comparison is not tuned in advance.')}
              </Hinweis>
            </div>
          )}

          <Versprechen l={lauf} />

          {lauf.rueckmeldungen.length > 0 && (
            <Abschnitt
              titel={t('Was die Personas einwenden', 'What the personas object to')}
              unter={lauf.mode === 'feedback' ? t('Wer aussteigt, steht zuerst.', 'Those who drop out come first.')
                : lauf.stufe === 'full' ? t('Zur geprüften Fassung, die niedrigste Note zuerst.', 'On the tested version, lowest score first.') : undefined}
            >
              <Panel>
                <ul className="m-0 list-none p-0">
                  {kernZuerst(lauf.rueckmeldungen).map((r, i) => <Stimme key={`${r.persona}-${i}`} r={r} />)}
                </ul>
              </Panel>
              <WeiterInClaude l={lauf} />
            </Abschnitt>
          )}

          <NotenZugeklappt l={lauf} />

          <p className="mt-10 mb-0 text-[0.8125rem] leading-relaxed [overflow-wrap:anywhere] text-text-hell">
            {[lauf.study ? t(`Studie ${lauf.study}`, `Study ${lauf.study}`) : null, lauf.set_version ? t(`Fassung ${lauf.set_version} der Zielgruppe`, `Audience version ${lauf.set_version}`) : null, lauf.model, `Run ${lauf.id}`].filter(Boolean).join(' · ')}
          </p>
        </>
      )}
    </AppRahmen>
  );
}

function Fehler({ f }: { f: ApiFehler }) {
  const text = f.status === 404 && f.code === 'Not found'
    ? t('Die Detailansicht ist noch nicht freigeschaltet. Urteil und Kosten jedes Runs stehen weiter in der Liste.', 'The detail view is not enabled yet. The verdict and cost of every run are still in the list.')
    : f.status === 404
      ? t('Diesen Run gibt es hier nicht. Entweder gehört er zu einem anderen Konto, oder die Adresse ist unvollständig.', 'This run does not exist here. Either it belongs to another account, or the address is incomplete.')
      : f.message;
  return (
    <>
      <Seitenkopf titel="Run" />
      <Panel className="px-4 py-5 sm:px-5">
        <p className="m-0 text-[0.9375rem] leading-relaxed text-text-leise">{text}</p>
        <p className="mt-3 mb-0 text-[0.9375rem]"><Link to="/runs" className="text-marke-tief">{t('Zu den Runs', 'Go to runs')}</Link></p>
      </Panel>
    </>
  );
}

/** Set und Fassung, Textart, Zeitpunkt. Umbruch nur zwischen den Teilen. */
function Kopfzeile({ l }: { l: Detail }) {
  const teile = [
    textart()[l.asset] ?? l.asset,
    l.set_name || l.set,
    zeitpunkt(l.created_at),
  ].filter((t): t is string => !!t);
  return (
    <>
      {teile.map((t, i) => (
        <span key={i}>
          {/* Geschuetztes Leerzeichen vor dem Punkt: umbrochen wird nur danach,
              nie vor einem „·" am Zeilenanfang. Lange Namen brechen an ihren
              eigenen Leerzeichen um, statt abgeschnitten zu werden; kurze (Datum
              mit Uhrzeit) bleiben ganz. */}
          <span className={t.length <= 24 ? 'whitespace-nowrap' : '[overflow-wrap:anywhere]'}>{t}</span>
          {i < teile.length - 1 && <span aria-hidden>{'\u00a0· '}</span>}
        </span>
      ))}
    </>
  );
}

/**
 * Das Urteil als Satz mit Zahl, wie im Bericht. `knapp` kommt vom Server
 * (dieselbe Rauschschwelle wie in Claude): dann gibt es keinen Sieger, egal
 * was `verdict` sagt.
 */
function Urteil({ l, markt, texte }: { l: Detail; markt: ReactNode; texte?: ReactNode }) {
  // Der Text steht als Titel darueber; der Fassungsname („f3-ohne-technik")
  // las sich im Satz wie ein Fehler. „Geprüft" wie in der Notentabelle, die
  // der Bericht mitbenutzt.
  const name = istSeitentest(l.variant) ? t('Die neue Fassung (B)', 'The new version (B)') : t('Die geprüfte Fassung', 'The tested version');
  const hl = 'whitespace-nowrap text-marke italic tabular-nums';
  let marke: ReactNode;
  let satz: ReactNode;
  const zahlen: Array<[string, string]> = [];
  // Mit Kern und Rand gelten Stimmen, Punkte und Urteil fuer den Kern
  // (readRun, kern.ts). Ohne Randpersona ist kr null und alles bleibt wie vorher.
  const kr = l.mode === 'compare' && l.stufe === 'full' ? leseKernRand(l) : null;
  const imKern = kr ? t(' im Kern', ' in the core') : '';
  const muster = leseMuster(l);
  let unter: ReactNode = null;

  if (l.mode === 'feedback') {
    const kern = l.rueckmeldungen.filter(r => r.rolle !== 'rand');
    const rand = l.rueckmeldungen.filter(r => r.rolle === 'rand');
    const getrennt = kern.length > 0 && rand.length > 0;
    const basis = getrennt ? kern : l.rueckmeldungen;
    const weiter = basis.filter(r => r.weiter === true).length;
    marke = <Marke>{t('ohne Vergleich', 'no comparison')}</Marke>;
    satz = getrennt
      ? t(
        <><span className={hl}>{weiter} von {kern.length}</span> Personas im Kern würden den nächsten Schritt gehen.</>,
        <><span className={hl}>{weiter} of {kern.length}</span> core personas would take the next step.</>,
      )
      : t(
        <><span className={hl}>{weiter} von {l.personas}</span> Personas würden den nächsten Schritt gehen.</>,
        <><span className={hl}>{weiter} of {l.personas}</span> personas would take the next step.</>,
      );
    if (getrennt) {
      const weiterRand = rand.filter(r => r.weiter === true).length;
      unter = <p className="mt-3 mb-0 text-[0.875rem] text-text-leise">{t(
        <>Erweiterte Zielgruppe: {weiterRand} von {rand.length} gehen weiter. Zählt nicht ins Urteil.</>,
        <>Extended audience: {weiterRand} of {rand.length} go on. Does not count toward the verdict.</>,
      )}</p>;
    }
  } else if (l.stufe === 'quick' || !l.scores) {
    marke = <Marke>{t('Schnelltest', 'Quick test')}</Marke>;
    satz = t(
      <>Schnelltest ohne Noten. {l.personas === 1 ? 'Eine Persona hat' : `${l.personas} Personas haben`} reagiert.</>,
      <>Quick test without scores. {l.personas === 1 ? 'One persona' : `${l.personas} personas`} responded.</>,
    );
  } else {
    const s = l.stimmen;
    const stand = s ? (l.verdict === 'baseline' ? `${s.baseline} : ${s.candidate}` : `${s.candidate} : ${s.baseline}`) : null;
    if (l.knapp) {
      marke = <Marke>{t('zu knapp', 'too close')}{imKern}</Marke>;
      satz = <>{t('Zu knapp für ein Urteil', 'Too close for a verdict')}{imKern}{stand ? <>: <span className={hl}>{stand}</span></> : null}.</>;
    } else if (l.verdict === 'candidate') {
      marke = <Marke art="gut">{t('gewinnt', 'wins')}{imKern}</Marke>;
      satz = <>{name} {t('gewinnt', 'wins')}{imKern}{stand ? <> <span className={hl}>{stand}</span></> : null}.</>;
    } else if (l.verdict === 'baseline') {
      marke = <Marke art="schlecht">{t('verliert', 'loses')}{imKern}</Marke>;
      satz = t(
        <>Die Vergleichsfassung liegt{imKern} vorn{stand ? <>, <span className={hl}>{stand}</span></> : null}.</>,
        <>The baseline is ahead{imKern}{stand ? <>, <span className={hl}>{stand}</span></> : null}.</>,
      );
    } else {
      marke = <Marke>{t('unentschieden', 'undecided')}{imKern}</Marke>;
      satz = <>{t('Kein klares Urteil', 'No clear verdict')}{imKern}{stand ? <>: <span className={hl}>{stand}</span></> : null}.</>;
    }
    // Mit beiden Fassungen in der Karte stehen die Punkte an den Texten.
    if (!(l.texte.geprueft && l.texte.vergleich)) zahlen.push([kr ? t('Punkte im Kern', 'Points in the core') : t('Punkte', 'Points'), t(`${zahl(l.scores.candidate)} zu ${zahl(l.scores.baseline)}`, `${zahl(l.scores.candidate)} to ${zahl(l.scores.baseline)}`)]);
    if (typeof l.margin === 'number') zahlen.push([t('Abstand', 'Margin'), `${l.margin > 0 ? '+' : l.margin < 0 ? '−' : '±'}${zahl(Math.abs(l.margin))}`]);
  }
  zahlen.push(kr ? [t('Kern + erweitert', 'Core + extended'), `${kr.kern.n} + ${kr.rand.n}`] : [t('Personas', 'Personas'), String(l.personas)], [t('Credits', 'Credits'), String(l.credits)]);

  return (
    <Panel className="p-4 sm:p-6">
      {marke}
      <p className="mt-3 mb-0 max-w-[26ch] font-serif text-[1.625rem] leading-[1.15] font-semibold tracking-[-0.015em] text-tinte [text-wrap:balance] sm:text-[2rem]">
        {satz}
      </p>
      {!l.knapp && l.score_im_rauschen && (
        <p className="mt-2 mb-0 text-[0.8125rem] text-text-hell">{t('Die Stimmen sind klar, der Punktabstand ist gering.', 'The votes are clear, the points margin is small.')}</p>
      )}
      {texte}
      <dl className="mt-5 mb-0 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-linie-zart pt-4 min-[26rem]:flex min-[26rem]:flex-wrap min-[26rem]:gap-x-8">
        {zahlen.map(([k, v]) => (
          <div key={k} className="flex flex-col-reverse">
            <dt className="text-[0.8125rem] text-text-hell">{k}</dt>
            <dd className="m-0 text-[1.0625rem] font-semibold text-tinte tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
      {kr && <p className="mt-3 mb-0 text-[0.875rem] text-text-leise">{randZeile(kr)}</p>}
      {unter}
      {kr?.warnung && (
        <p role="alert" className="mt-4 mb-0 rounded-lg border border-schlecht/25 bg-schlecht/[0.06] px-3.5 py-3 text-[0.875rem] leading-relaxed text-schlecht">
          {kr.warnung}
        </p>
      )}
      {muster && (
        <p className="mt-3 mb-0 rounded-lg border border-linie bg-ebene px-3.5 py-3 text-sm leading-relaxed text-text-leise">
          {musterSatz(muster)}
        </p>
      )}
      {markt}
    </Panel>
  );
}

/**
 * Das Markenversprechen des Laufs und wie viele Personas sagen, dass der Text
 * es hält. Zählt nicht in den Score, und das steht dabei.
 */
function Versprechen({ l }: { l: Detail }) {
  const v = l.markenversprechen;
  if (!v?.text) return null;
  const b = leseBilanz(v);
  return (
    <Abschnitt titel={t('Markenversprechen', 'Brand promise')}>
      <Panel className="px-4 py-4 sm:px-5">
        <blockquote className="m-0 font-serif text-[1.1875rem] leading-snug font-semibold text-tinte [overflow-wrap:anywhere]">{t(<>„{v.text}“</>, <>“{v.text}”</>)}</blockquote>
        <p className="mt-2.5 mb-0 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[0.8125rem] text-text-leise">
          {b && (
            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-linie-zart" aria-hidden>
              <span className="block h-full rounded-full bg-marke" style={{ width: `${(b.haelt / b.von) * 100}%` }} />
            </span>
          )}
          <span>{b ? bilanzSatz(b) : t('Keine Persona hat dazu etwas gesagt.', 'No persona said anything about it.')} {t('Zählt nicht in den Score.', 'Does not count toward the score.')}</span>
        </p>
      </Panel>
    </Abschnitt>
  );
}

/** Marktergebnis, wie in der Liste. Nur bei vollen Vergleichen. */
function Markt({ l, melden }: { l: Detail; melden: () => void }) {
  const e = l.ergebnis;
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 rounded-md bg-ebene px-3.5 py-3 text-[0.875rem] text-text-leise">
      {e ? (
        <>
          <span className="inline-flex flex-wrap items-center gap-2">
            {t('Im Markt:', 'In the market:')}
            {!e.zaehlbar ? <Marke>{t('zählt nicht', 'does not count')}</Marke> : e.treffer ? <Marke art="gut">{t('Treffer', 'Hit')}</Marke> : <Marke art="schlecht">{t('daneben', 'Miss')}</Marke>}
            <span className="basis-full text-[0.8125rem] text-text-hell sm:basis-auto">{e.grund}</span>
          </span>
          <KleinerKnopf type="button" onClick={melden}>{t('Ändern', 'Change')}</KleinerKnopf>
        </>
      ) : (
        <>
          <span>{t('Wie lief es im Markt? Melden kostet nichts.', 'How did it do in the market? Reporting is free.')}</span>
          <KleinerKnopf type="button" onClick={melden} className="!border-knopf !bg-knopf !text-auf-marke hover:!bg-knopf-hover">{t('Ergebnis melden', 'Report the result')}</KleinerKnopf>
        </>
      )}
    </div>
  );
}

function Stimme({ r }: { r: Rueckmeldung }) {
  return (
    <li className="grid gap-x-6 gap-y-2 border-t border-linie-zart px-4 py-4 first:border-t-0 sm:grid-cols-[12rem_1fr] sm:px-5">
      <div className="min-w-0">
        <div className="text-[0.9375rem] font-semibold text-tinte [overflow-wrap:anywhere]">
          {r.persona}
          {r.rolle === 'rand' && <RandEtikett />}
        </div>
        {r.note != null && (
          <div className="mt-1.5 flex items-center gap-2">
            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-linie-zart" aria-hidden>
              <span className="block h-full rounded-full bg-marke" style={{ width: `${Math.min(100, Math.max(0, r.note * 10))}%` }} />
            </span>
            <span className="text-[0.8125rem] text-text-hell tabular-nums">{t(`${zahl(r.note)} von 10`, `${zahl(r.note)} of 10`)}</span>
          </div>
        )}
        {r.weiter != null && (
          <div className="mt-1.5">{r.weiter ? <Marke art="gut">{t('geht weiter', 'goes on')}</Marke> : <Marke art="schlecht">{t('steigt aus', 'drops out')}</Marke>}</div>
        )}
      </div>
      <div className="min-w-0 text-[0.9375rem] leading-relaxed [overflow-wrap:anywhere]">
        {r.einwand && <p className="m-0 text-text">{r.einwand}</p>}
        {r.unklar && <p className="mt-1 mb-0 text-[0.875rem] text-text-hell">{t('Unklar:', 'Unclear:')} {r.unklar}</p>}
        {r.absprung && <p className="mt-1 mb-0 text-[0.875rem] text-text-hell">{t('Ausgestiegen:', 'Dropped out:')} {r.absprung}</p>}
        {r.vorschlag && (
          <p className="mt-2.5 mb-0 rounded-md bg-ebene px-3 py-2 text-[0.875rem] text-text"><span className="font-medium text-tinte">{t('Vorschlag: ', 'Suggestion: ')}</span>{r.vorschlag}</p>
        )}
        {r.versprechen && (
          <p className="mt-2.5 mb-0 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[0.8125rem] text-text-leise">
            <Marke art={r.versprechen.haelt ? 'gut' : 'neutral'}>{r.versprechen.haelt ? t('hält das Versprechen', 'keeps the promise') : t('driftet ab', 'drifts off')}</Marke>
            {r.versprechen.grund && <span className="min-w-0">{r.versprechen.grund}</span>}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * Beide Fassungen im Wortlaut, in der Urteilskarte (Entwurf 1c), seit
 * 27.10.2026 Abschnitt für Abschnitt mit Rolle und markierten Wörtern
 * (fassungen-ansicht.tsx). Die Punkte stehen am Kopf der Spalten, mit
 * denselben Zeichen wie in der Liste: voll die geprüfte Fassung, hohl der
 * Vergleich.
 */
function FassungenImUrteil({ l }: { l: Detail }) {
  const { vergleich, vergleich_erzeugt, quelle_url } = l.texte;
  const url = sichererLink(quelle_url);
  // Im Seitentest ist die gepruefte Fassung das von 99takes geschriebene B,
  // die Seite unter der URL ist die Vergleichsfassung A.
  const seitentest = istSeitentest(l.variant);
  const vonDerSeite = url ? <>{t('Von der Seite', 'From the page')} <a href={url} rel="noopener noreferrer nofollow" className="text-marke-tief [overflow-wrap:anywhere]">{url}</a></> : null;
  return (
    <FassungenAnsicht
      texte={l.texte}
      name={fassungsname(l.variant)}
      titelNeu={seitentest ? t('Neue Fassung (B)', 'New version (B)') : vergleich ? t('Geprüft', 'Tested') : t('Geprüfter Text', 'Tested copy')}
      titelAlt={seitentest ? t('Heutige Seite (A)', 'Current page (A)') : t('Vergleich', 'Baseline')}
      punkte={l.mode === 'compare' && l.stufe === 'full' ? l.scores : null}
      zusatzNeu={seitentest ? t('Von 99takes geschrieben, aus der Recherche deiner Zielgruppe.', 'Written by 99takes, from the research on your audience.') : vergleich ? null : vonDerSeite}
      zusatzAlt={seitentest ? vonDerSeite : vergleich_erzeugt ? t('Von 99takes erzeugt, weil keine mitgegeben war.', 'Generated by 99takes, because none was provided.') : null}
    />
  );
}

/**
 * Noten je Persona als Beleg, zugeklappt (Entwurf 1c). Die Zeile nennt den
 * Satz, die Tabelle steht beim Aufklappen darunter, ohne Karte in der Karte.
 */
function NotenZugeklappt({ l }: { l: Detail }) {
  const noten = personaNoten(l);
  if (noten.length === 0) return null;
  return (
    <details className="group mt-10 [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex cursor-pointer list-none flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-karte border border-linie bg-flaeche px-4 py-3.5 shadow-flaeche hover:bg-ebene group-open:mb-3 sm:px-5">
        <span className="inline-flex items-center gap-2 text-[0.9375rem] font-semibold text-tinte">
          <span aria-hidden className="inline-block text-text-hell transition-transform duration-200 group-open:rotate-90">▸</span>
          {t('Noten je Persona', 'Scores by persona')}
        </span>
        <span className="text-[0.8125rem] text-text-hell">{notenSatz(noten)}</span>
      </summary>
      <NotenJePersona quelle={l} ohneSatz />
    </details>
  );
}

/**
 * Was der Titel der Seite ist: der gepruefte Text, erste Zeile. Ohne Text der
 * Fassungsname, wie bisher.
 */
export function titelVon(l: Pick<Detail, 'text' | 'texte' | 'variant'>): string {
  const erste = (l.text ?? l.texte.geprueft ?? '').split('\n').map(z => z.trim()).find(Boolean);
  if (erste) {
    const kurz = erste.length > 140 ? `${erste.slice(0, 139).trimEnd()} …` : erste;
    return t(`„${kurz}“`, `“${kurz}”`);
  }
  return fassungsname(l.variant) || t('Run ohne Fassungsnamen', 'Run without a version name');
}

/**
 * Der naechste Schritt, als Satz fuer Claude (03.10.2026). Nach einem Run
 * ging es bisher nur in der Unterhaltung weiter, in der er lief. Der Satz ist
 * eine echte Anweisung: `write_challenger` nimmt einen `run` und schreibt aus
 * dessen Einwaenden (angebotNachRun im Backend). Nur bei Runs mit Einwaenden.
 */
function WeiterInClaude({ l }: { l: Detail }) {
  if (l.mode === 'vorauswahl' || !l.rueckmeldungen.some(r => r.einwand || r.vorschlag)) return null;
  return (
    <div className="mt-3 rounded-karte border border-dashed border-marke/45 bg-marke/[0.04] p-3.5 sm:p-4">
      <p className="mt-0 mb-2.5 text-[0.8125rem] leading-relaxed text-text-leise">
        {t(
          <><span className="font-semibold text-tinte">Weiter in Claude:</span> neue Fassungen aus genau diesen Einwänden,
          {' '}{CHALLENGER.von} bis {CHALLENGER.bis} Credits. Claude nennt den Preis vorher.</>,
          <><span className="font-semibold text-tinte">Continue in Claude:</span> new versions from exactly these objections,
          {' '}{CHALLENGER.von} to {CHALLENGER.bis} credits. Claude states the price first.</>,
        )}
      </p>
      <Kopierzeile art="text" text={t(`Schreib mir aus den Einwänden von Run ${l.id} drei neue Fassungen.`, `Write me three new versions from the objections of run ${l.id}.`)} />
    </div>
  );
}
